/**
 * The slots the workbook contradicts itself on (f8.3).
 *
 * `US Projects` is flat -- 70 engagement rows, each repeating the group's DEM
 * and CEM and the unit's CM -- while four of the five delivery roles attach
 * ABOVE the engagement. The rows disagree on fourteen (node, role) slots, and
 * the database accepts exactly one current holder per slot. Somebody has to
 * decide which.
 *
 * **Nothing is pre-selected, and the count is evidence rather than a
 * recommendation.** Thirteen of the fourteen real contests have a strict
 * majority; one does not -- `CU US Banking and Insurance`'s CM is named on 4
 * rows of 11, a 36 per cent plurality against three other managers. A rule
 * that is right thirteen times and silently wrong once, on the largest
 * business unit in the estate, is worse than no rule, because nobody would
 * look. So the candidates are ranked by source-row count, the count is
 * rendered, and no radio starts checked.
 *
 * **The losing candidates are not deleted** when a choice is made -- what the
 * workbook said and what the human decided both stay readable, and f8.2's
 * `WARNING` finding naming every candidate is untouched.
 */

import { AlertTriangle, Check } from 'lucide-react';

import { ApiError, type ContestedSlot, type LookupValue } from '@/api/client';
import { useChooseContestedHolder } from '@/api/queries';

import { levelLabel } from './importView';
import { candidateName, evidenceLabel, initialsOf, roleLabel } from './resolutionView';

function SlotDecision({
  batchId,
  slot,
  deliveryRoles,
  disabled,
}: {
  batchId: string;
  slot: ContestedSlot;
  deliveryRoles: readonly LookupValue[];
  disabled: boolean;
}) {
  const choose = useChooseContestedHolder(batchId);
  const chosen = slot.candidates.find((candidate) => candidate.is_chosen);

  return (
    <div className="stack-sm contested-slot">
      <div className="row">
        <span className="badge badge-chip">
          {roleLabel(slot.delivery_role_code, deliveryRoles)}
        </span>
        <span className="truncate">
          <b>{slot.node_name}</b>
          <span className="sub">{levelLabel(slot.node_level)}</span>
        </span>
        {slot.is_decided ? (
          <span className="badge pill-success">
            <Check aria-hidden="true" /> Decided
          </span>
        ) : (
          <span className="badge pill-danger">Needs a decision</span>
        )}
      </div>

      <p className="hint">
        The workbook names {slot.candidates.length} holders for this record across{' '}
        {slot.total_source_rows} source rows. Exactly one may hold it, and the counts
        below are what the rows said — not a recommendation.
      </p>

      <div
        className="stack-sm"
        role="radiogroup"
        aria-label={`Holder of ${slot.delivery_role_code} on ${slot.node_name}`}
      >
        {slot.candidates.map((candidate) => {
          const name = candidateName(candidate);
          return (
            <label
              key={candidate.staged_role_id}
              className={`radio-card${candidate.is_chosen ? ' is-selected' : ''}`}
            >
              <input
                type="radio"
                name={`contest-${slot.staged_node_id}-${slot.delivery_role_code}`}
                value={candidate.staged_role_id}
                /* Nothing is checked until a human checks it: `is_chosen` is
                   false on every row of an undecided slot, by design. */
                checked={candidate.is_chosen}
                disabled={disabled || choose.isPending}
                onChange={() => choose.mutate(candidate.staged_role_id)}
              />
              <span className="avatar sm" aria-hidden="true">
                {initialsOf(name)}
              </span>
              <span className="rc-text">
                <span className="rc-title">{name}</span>
                <span className="rc-desc">
                  {evidenceLabel(candidate.source_row_count, slot.total_source_rows)}
                  {candidate.bound_user ? ` · ${candidate.bound_user.email}` : ''}
                </span>
              </span>
            </label>
          );
        })}
      </div>

      {chosen && !choose.isPending && (
        <p className="hint">
          {candidateName(chosen)} will hold it. The other{' '}
          {slot.candidates.length - 1 === 1 ? 'candidate is' : 'candidates are'} kept
          on the record, so what the workbook said stays readable.
        </p>
      )}

      {choose.error instanceof ApiError && (
        <div className="alert alert-danger" role="alert">
          <AlertTriangle aria-hidden="true" />
          <div>
            <div className="a-body">{choose.error.message}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export function ContestedSlots({
  batchId,
  slots,
  deliveryRoles,
  disabled,
}: {
  batchId: string;
  slots: readonly ContestedSlot[];
  deliveryRoles: readonly LookupValue[];
  /** True once the batch has left the states its plan may be decided in. */
  disabled: boolean;
}) {
  const undecided = slots.filter((slot) => !slot.is_decided).length;

  return (
    <div className="card">
      <div className="card-head">
        <h3>4 &middot; Records the workbook names more than one holder for</h3>
        {undecided > 0 ? (
          <span className="badge pill-danger">{undecided} undecided</span>
        ) : (
          <span className="badge pill-success">All decided</span>
        )}
      </div>
      <div className="card-body stack">
        {slots.length === 0 ? (
          <p className="hint">
            Every record in this workbook names one holder per delivery role. Nothing
            to decide here.
          </p>
        ) : (
          slots.map((slot) => (
            <SlotDecision
              key={`${slot.staged_node_id}-${slot.delivery_role_code}`}
              batchId={batchId}
              slot={slot}
              deliveryRoles={deliveryRoles}
              disabled={disabled}
            />
          ))
        )}
      </div>
    </div>
  );
}
